/**
 * In-app manual popup.
 *
 * Loads static Markdown from public/help and renders a small subset:
 * headings, paragraphs, unordered lists, links, and fenced code.
 */

import { currentLocale, t } from '../i18n';
import { openShortcutsPopup } from './shortcuts-popup';

let closeManual: (() => void) | null = null;

const MANUAL_FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function manualUrl(locale: 'en' | 'ja'): string {
  const file = locale === 'ja' ? 'MANUAL.ja.md' : 'MANUAL.md';
  const base = import.meta.env.BASE_URL.endsWith('/')
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  return `${base}help/${file}`;
}

export async function loadManualMarkdown(locale = currentLocale()): Promise<string> {
  const primary = await fetch(manualUrl(locale));
  if (primary.ok) return primary.text();
  if (locale !== 'en') {
    const fallback = await fetch(manualUrl('en'));
    if (fallback.ok) return fallback.text();
  }
  throw new Error(`Manual not found for locale: ${locale}`);
}

const MANUAL_SECTION_IDS = [
  'image-load-workflow',
  'segmentation-flow',
  'region-tools',
  'tone-presets',
  'source-adjust-per-region',
  'keyboard-shortcuts',
  'export-sizes',
  'neo-brutalist-ui-conventions',
] as const;

function appendInlineMarkdown(parent: HTMLElement, text: string): void {
  const linkPattern = /\[([^\]]+)\]\((#[^)]+|amitone:shortcuts)\)/g;
  let lastIndex = 0;
  for (const match of text.matchAll(linkPattern)) {
    if (match.index > lastIndex) {
      parent.append(document.createTextNode(text.slice(lastIndex, match.index)));
    }
    const a = document.createElement('a');
    const href = match[2] ?? '#';
    a.href = href === 'amitone:shortcuts' ? '#' : href;
    a.textContent = match[1] ?? '';
    if (href === 'amitone:shortcuts') {
      a.dataset['manualAction'] = 'shortcuts';
      a.addEventListener('click', (event) => {
        event.preventDefault();
        openShortcutsPopup(a);
      });
    }
    parent.appendChild(a);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parent.append(document.createTextNode(text.slice(lastIndex)));
  }
}

function getManualFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(MANUAL_FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export function renderManualMarkdown(markdown: string): HTMLElement {
  const root = document.createElement('div');
  root.className = 'manual-content';
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  let paragraph: string[] = [];
  let list: HTMLUListElement | null = null;
  let code: string[] | null = null;
  let sectionIndex = 0;

  function flushParagraph(): void {
    if (paragraph.length === 0) return;
    const p = document.createElement('p');
    appendInlineMarkdown(p, paragraph.join(' '));
    root.appendChild(p);
    paragraph = [];
  }

  function flushList(): void {
    if (list === null) return;
    root.appendChild(list);
    list = null;
  }

  for (const line of lines) {
    if (code !== null) {
      if (line.trim() === '```') {
        const pre = document.createElement('pre');
        const codeEl = document.createElement('code');
        codeEl.textContent = code.join('\n');
        pre.appendChild(codeEl);
        root.appendChild(pre);
        code = null;
      } else {
        code.push(line);
      }
      continue;
    }

    if (line.trim() === '```') {
      flushParagraph();
      flushList();
      code = [];
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1]!.length;
      const el = document.createElement(level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3');
      const text = heading[2]!.trim();
      el.textContent = text;
      if (level === 2) {
        el.id = MANUAL_SECTION_IDS[sectionIndex] ?? `section-${sectionIndex + 1}`;
        sectionIndex++;
      }
      root.appendChild(el);
      continue;
    }

    const bullet = /^-\s+(.+)$/.exec(line);
    if (bullet) {
      flushParagraph();
      list ??= document.createElement('ul');
      const li = document.createElement('li');
      appendInlineMarkdown(li, bullet[1]!.trim());
      list.appendChild(li);
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();
  if (code !== null) {
    const pre = document.createElement('pre');
    const codeEl = document.createElement('code');
    codeEl.textContent = code.join('\n');
    pre.appendChild(codeEl);
    root.appendChild(pre);
  }
  return root;
}

export function openManualPopup(anchor: HTMLElement): void {
  closeManual?.();

  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  const backdrop = document.createElement('div');
  backdrop.className = 'manual-popup-backdrop';

  const popup = document.createElement('div');
  popup.className = 'settings-popup manual-popup';
  popup.tabIndex = -1;
  popup.setAttribute('role', 'dialog');
  popup.setAttribute('aria-modal', 'true');
  popup.setAttribute('aria-labelledby', 'manual-popup-title');

  const header = document.createElement('div');
  header.className = 'manual-popup-header';

  const title = document.createElement('div');
  title.id = 'manual-popup-title';
  title.className = 'settings-popup-label manual-popup-title';
  title.textContent = t('manual.title');
  header.appendChild(title);

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'btn btn-ghost manual-popup-close';
  closeButton.textContent = t('manual.close');
  closeButton.setAttribute('aria-label', t('manual.close'));
  header.appendChild(closeButton);
  popup.appendChild(header);

  const body = document.createElement('div');
  body.className = 'manual-popup-body';
  body.textContent = t('manual.loading');
  popup.appendChild(body);

  backdrop.appendChild(popup);
  document.body.appendChild(backdrop);

  void loadManualMarkdown()
    .then((markdown) => {
      body.replaceChildren(renderManualMarkdown(markdown));
    })
    .catch(() => {
      body.textContent = t('manual.error');
    });

  function close(): void {
    if (closeManual !== close) return;
    closeManual = null;
    document.removeEventListener('click', handleOutside, true);
    document.removeEventListener('keydown', handleKey, true);
    backdrop.remove();
    const restoreTarget = anchor.isConnected ? anchor : previouslyFocused;
    if (restoreTarget?.isConnected) {
      restoreTarget.focus({ preventScroll: true });
    }
  }

  function handleOutside(event: MouseEvent): void {
    if (event.target === backdrop) {
      close();
    }
  }

  function handleKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }

    if (event.key === 'Tab') {
      const focusable = getManualFocusable(popup);
      if (focusable.length === 0) {
        event.preventDefault();
        popup.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (!popup.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }
  }

  setTimeout(() => {
    if (closeManual !== close) return;
    document.addEventListener('click', handleOutside, true);
    document.addEventListener('keydown', handleKey, true);
  }, 0);

  closeButton.addEventListener('click', close);
  closeButton.focus({ preventScroll: true });
  closeManual = close;
}
